import { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import { ApiRequestError } from '@/lib/api/client';

/**
 * 위젯 한 칸의 껍데기. front 의 같은 이름 컴포넌트를 옮겼다(DOC-82 §6-2).
 *
 * <p>위젯은 <b>서로를 기다리지 않는다.</b> 각자 자기 쿼리를 갖고, 자기 자리에서 스켈레톤을
 * 돌리다 자기 자리만 채운다 — 하나가 느리면 열 개가 다 늦게 뜨는 화면은 「느린 화면」으로
 * 기억된다. 그래서 로딩·오류·빈 목록 세 상태를 여기서 처리하고, 위젯은 「무엇을 그릴지」만 적는다.
 */

const BODY_MIN_HEIGHT = 88;

export function WidgetFrame({
  title,
  icon,
  to,
  toLabel = '전체보기',
  headerRight,
  pending = false,
  error = null,
  empty = false,
  emptyText = '표시할 내용이 없습니다.',
  onRetry,
  children,
}: {
  title: string;
  icon: string;
  to?: string;
  toLabel?: string;
  headerRight?: ReactNode;
  pending?: boolean;
  error?: Error | null;
  empty?: boolean;
  /**
   * 비어 있을 때의 문구. <b>위젯마다 자기 말로 적는다</b> — 「표시할 내용이 없습니다」 는
   * 무엇이 없는지 답하지 않아 사용자가 고장으로 읽는다.
   */
  emptyText?: string;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  const failure = error ? describeError(error, emptyText) : null;

  return (
    <section className="card flex flex-col">
      <div className="mb-3 flex items-center gap-2">
        <span
          className="flex flex-shrink-0 items-center justify-center"
          style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent)' }}
        >
          <MenuIcon name={icon} size={15} />
        </span>
        <h2 className="truncate text-ui-lg font-semibold">{title}</h2>
        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          {headerRight}
          {to !== undefined && (
            <Link to={to} className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
              {toLabel} ›
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1" style={{ minHeight: BODY_MIN_HEIGHT }}>
        {pending ? (
          <WidgetSkeleton />
        ) : failure !== null ? (
          <WidgetMessage action={failure.retryable && onRetry ? <RetryButton onClick={onRetry} /> : undefined}>
            {failure.text}
          </WidgetMessage>
        ) : empty ? (
          <WidgetMessage>{emptyText}</WidgetMessage>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function WidgetSkeleton() {
  return (
    <div className="space-y-2.5 pt-1" aria-hidden>
      {[80, 100, 65, 90].map((width, index) => (
        <div key={index} style={{ width: `${width}%`, height: 12, borderRadius: 6, background: 'var(--bg-hover)' }} />
      ))}
    </div>
  );
}

/**
 * 실패를 사람 말로 바꾼다.
 *
 * <p>모든 실패가 사고는 아니다 — 볼 자료가 없거나(404) 권한이 없으면(403) 사용자가 할 수 있는
 * 일이 없다. 그런 상태에 「불러오지 못했습니다」 를 띄우면 멀쩡한 화면이 고장 난 것처럼 보인다.
 *
 * <p><b>외부 사용자에게는 사유를 더 줄인다.</b> 내부 화면은 개발 중 HTTP 코드를 곁들이지만
 * 여기서는 그것도 없다 — 오류 코드가 시스템의 모양을 알려 주는 통로가 되지 않게 한다.
 */
function describeError(error: Error, emptyText: string): { text: string; retryable: boolean } {
  if (error instanceof ApiRequestError) {
    if (error.status === 404) return { text: emptyText, retryable: false };
    if (error.status === 403) return { text: '볼 수 있는 권한이 없습니다.', retryable: false };
    if (error.status === 401) return { text: '로그인이 만료되었습니다. 다시 로그인해 주세요.', retryable: false };
  }
  return { text: '내용을 가져오는 중입니다.', retryable: true };
}

function WidgetMessage({ action, children }: { action?: ReactNode; children: ReactNode }) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 px-2 text-center text-ui-sm"
      style={{ color: 'var(--text-muted)' }}
    >
      <span>{children}</span>
      {action}
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="btn-secondary px-2.5 py-1 text-ui-2xs">
      다시 시도
    </button>
  );
}

/**
 * 위젯 하나가 렌더 중 예외를 던져도 화면 전체가 하얗게 되지 않도록 막는다.
 *
 * <p>쿼리 실패는 {@link WidgetFrame} 의 `error` 가 받지만, 응답 모양이 예상과 달라 그리는 도중
 * 터지는 경우는 잡지 못한다. 그때 화면 전체가 사라지면 다른 곳으로 갈 수도 없다.
 */
export class WidgetBoundary extends Component<{ title: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error(`[widget] ${this.props.title}`, error);
  }

  render() {
    if (this.state.failed) {
      return (
        <WidgetFrame
          title={this.props.title}
          icon="grid"
          error={new Error('render failed')}
          onRetry={() => this.setState({ failed: false })}
        />
      );
    }
    return this.props.children;
  }
}

/** 위젯 안에서 반복되는 목록 한 줄. */
export function WidgetRow({
  to,
  leading,
  title,
  meta,
  trailing,
}: {
  to?: string;
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
}) {
  const body = (
    <>
      {leading}
      <span className="min-w-0 flex-1 truncate text-ui-md" style={{ color: 'var(--text-secondary)' }}>
        {title}
      </span>
      {meta !== undefined && (
        <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--text-muted)' }}>
          {meta}
        </span>
      )}
      {trailing}
    </>
  );

  const className = 'flex items-center gap-2 rounded-md py-1.5';

  if (to !== undefined) {
    return (
      <Link to={to} className={`${className} hover:bg-[var(--bg-hover)]`}>
        {body}
      </Link>
    );
  }
  return <div className={className}>{body}</div>;
}

/** 위젯 안의 큰 숫자 한 칸. 기성·출역처럼 「값 하나」가 요점인 자리에 쓴다. */
export function WidgetStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-ui-2xl font-semibold">{value}</span>
      {sub !== undefined && (
        <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{sub}</span>
      )}
    </div>
  );
}
